import { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
} from '@material-ui/core';
import RefreshIcon from '@material-ui/icons/Refresh';
import SearchIcon from '@material-ui/icons/Search';
import { ToggleButton, ToggleButtonGroup } from '@material-ui/lab';
import { makeStyles } from '@material-ui/core/styles';
import type { LogEntry } from '../api/types';

const LEVEL_COLORS: Record<string, string> = {
  error: '#FF462D',
  warn: '#FFCB47',
  warning: '#FFCB47',
  info: '#4CDD84',
  debug: '#29707A',
};

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    maxHeight: 480,
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
    flexWrap: 'wrap',
  },
  timeToggle: {
    '& .MuiToggleButton-root': {
      padding: '4px 12px',
      fontSize: 12,
      textTransform: 'none',
    },
  },
  searchField: {
    minWidth: 180,
    '& .MuiOutlinedInput-root': {
      fontSize: 12,
    },
  },
  logList: {
    overflowY: 'auto',
    flex: 1,
    fontFamily: '"JetBrains Mono", "Fira Code", monospace',
    fontSize: 12,
    lineHeight: 1.6,
    padding: theme.spacing(1),
    backgroundColor:
      theme.palette.type === 'dark'
        ? 'rgba(255,255,255,0.03)'
        : 'rgba(0,0,0,0.02)',
    borderRadius: 4,
  },
  logLine: {
    display: 'flex',
    gap: theme.spacing(1),
    '&:hover': {
      backgroundColor:
        theme.palette.type === 'dark'
          ? 'rgba(255,255,255,0.05)'
          : 'rgba(0,0,0,0.04)',
    },
  },
  timestamp: {
    color: theme.palette.text.secondary,
    flexShrink: 0,
    whiteSpace: 'nowrap',
  },
  level: {
    flexShrink: 0,
    fontWeight: 600,
    width: 48,
    textAlign: 'right',
  },
  podName: {
    color: theme.palette.text.secondary,
    flexShrink: 0,
    whiteSpace: 'nowrap' as const,
    fontSize: 11,
  },
  line: {
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all',
  },
  empty: {
    padding: theme.spacing(3),
    textAlign: 'center',
    color: theme.palette.text.secondary,
  },
}));

interface Props {
  entries: LogEntry[];
  loading: boolean;
  onRefresh: () => void;
  activePreset: string;
  timeRangePresets: { label: string }[];
  onTimeRangeChange: (preset: string) => void;
  pods: string[];
  selectedPod: string;
  onPodChange: (pod: string) => void;
  selectedLevel: string;
  onLevelChange: (level: string) => void;
}

function extractLevel(entry: LogEntry): string {
  if (entry.labels.severity_text) return entry.labels.severity_text.toLowerCase();
  if (entry.labels.level) return entry.labels.level.toLowerCase();
  if (entry.labels.severity) return entry.labels.severity.toLowerCase();
  const match = entry.line.match(/\b(ERROR|WARN|WARNING|INFO|DEBUG|TRACE)\b/i);
  return match ? match[1].toLowerCase() : '';
}

export const LogsPanel = ({
  entries,
  loading,
  onRefresh,
  activePreset,
  timeRangePresets,
  onTimeRangeChange,
  pods,
  selectedPod,
  onPodChange,
  selectedLevel,
  onLevelChange,
}: Props) => {
  const classes = useStyles();
  const [expanded, setExpanded] = useState(false);
  const [search, setSearch] = useState('');

  const filteredEntries = useMemo(() => {
    let result = entries;
    if (selectedPod) {
      result = result.filter(
        e => (e.labels.k8s_pod_name ?? e.labels.pod ?? e.labels.pod_name) === selectedPod,
      );
    }
    if (selectedLevel) {
      result = result.filter(e => extractLevel(e) === selectedLevel);
    }
    if (search.trim()) {
      const term = search.toLowerCase();
      result = result.filter(e => e.line.toLowerCase().includes(term));
    }
    return result;
  }, [entries, selectedPod, selectedLevel, search]);

  const style = expanded ? { maxHeight: 800 } : {};

  return (
    <Box className={classes.container} style={style}>
      <Box className={classes.toolbar}>
        <ToggleButtonGroup
          className={classes.timeToggle}
          value={activePreset}
          exclusive
          onChange={(_, v) => v && onTimeRangeChange(v)}
          size="small"
        >
          {timeRangePresets.map(p => (
            <ToggleButton key={p.label} value={p.label}>
              {p.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        <TextField
          className={classes.searchField}
          variant="outlined"
          size="small"
          placeholder="Search logs…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        <Select
          value={selectedPod}
          onChange={e => onPodChange(e.target.value as string)}
          displayEmpty
          variant="outlined"
          style={{ fontSize: 12, minWidth: 160, height: 32 }}
        >
          <MenuItem value="">All pods</MenuItem>
          {pods.map(p => (
            <MenuItem key={p} value={p} style={{ fontSize: 12 }}>
              {p}
            </MenuItem>
          ))}
        </Select>

        <Select
          value={selectedLevel}
          onChange={e => onLevelChange(e.target.value as string)}
          displayEmpty
          variant="outlined"
          style={{ fontSize: 12, minWidth: 100, height: 32 }}
        >
          <MenuItem value="">All levels</MenuItem>
          {['error', 'warn', 'info', 'debug'].map(l => (
            <MenuItem key={l} value={l} style={{ fontSize: 12 }}>
              {l.toUpperCase()}
            </MenuItem>
          ))}
        </Select>

        <Tooltip title="Refresh logs">
          <IconButton size="small" onClick={onRefresh}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Typography
          variant="caption"
          color="textSecondary"
          style={{ marginLeft: 'auto', cursor: 'pointer' }}
          onClick={() => setExpanded(e => !e)}
        >
          {search.trim()
            ? `${filteredEntries.length} / ${entries.length} entries`
            : `${entries.length} entries`}{' '}
          {expanded ? '(collapse)' : '(expand)'}
        </Typography>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress size={24} />
        </Box>
      ) : filteredEntries.length === 0 ? (
        <Typography className={classes.empty} variant="body2">
          {search.trim() ? 'No matching log entries' : 'No log entries found'}
        </Typography>
      ) : (
        <Box className={classes.logList}>
          {filteredEntries.map((entry, i) => {
            const level = extractLevel(entry);
            return (
              <Box key={i} className={classes.logLine}>
                <span className={classes.timestamp}>
                  {new Date(entry.timestamp).toLocaleTimeString()}
                </span>
                {level && (
                  <span
                    className={classes.level}
                    style={{ color: LEVEL_COLORS[level] ?? undefined }}
                  >
                    {level.toUpperCase()}
                  </span>
                )}
                {(entry.labels.k8s_pod_name ?? entry.labels.pod ?? entry.labels.pod_name) && (
                  <span className={classes.podName}>
                    {entry.labels.k8s_pod_name ?? entry.labels.pod ?? entry.labels.pod_name}
                  </span>
                )}
                <span className={classes.line}>{entry.line}</span>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};
